/**
 * @module utils/PositionHelper
 * @description Handles the positioning of a "target" element relative to a
 * sibling element (e.g. a Dropdown Menu relative to its "trigger" button)
 * @todo If this code isn't robust enough, we could replace it with https://popper.js.org
 */
import { toArray } from "utils/utils";

/**
 * @class
 * @description Handles the positioning of a "target" element relative to a
 * sibling element (e.g. a Dropdown Menu relative to its "trigger" button)
 */
export default class PositionHelper {

  /**
   * Initialize the class instance
   * @param {HTMLElement} triggerEl - The trigger button
   * @param {HTMLElement} targetEl - The target element
   * @param {String} targetBaseClass - The target element's root/base class
   * @param {Object} [options={}] - Options for the instance
   * @param {Boolean} options.autoAdjustHorizontal - Whether to automatically adjust the position along the horizontal axis
   * @param {Boolean} options.autoAdjustVertical - Whether to automatically adjust the position along the vertical axis
   * of the target element to prevent it from being cut off by one of the viewport's edges
   */
  constructor(triggerEl, targetEl, targetBaseClass, options = {}) {
    if (!triggerEl || !targetEl || !targetBaseClass) {
      return;
    }
    this.trigger = triggerEl;
    this.target = targetEl;
    this.targetBaseClass = targetBaseClass;
    this.options = options;

    if (!this.target.classList.contains(targetBaseClass)) {
      return;
    }
    this.setPreferredPosition(this.getPosition().join("-"));
  }

  /**
   * Store the preferred menu position in a data attribute, for future reference
   * (this is only public because the specimen needs to dynamically change the value)
   * @public
   * @param {String} positionString - A hyphen-separated list of positions (e.g. "top-left")
   */
  setPreferredPosition(positionString) {
    this.target.dataset.pos = positionString;
  }

  /**
   * Apply the appropriate positioning classes and inline styles to the target
   * element then, if necessary, reposition it to ensure the it won't be cut
   * off by the edges of the viewport
   * @public
   */
  setPosition() {
    if (this.target.dataset.pos) {
      // Reset the position to its original, preferred value in case it
      // was previously altered in order to prevent viewport cutoff
      this.applyPositionClasses(...this.target.dataset.pos.split("-"));
    }
    this.applyPositionStyles();

    if (this.options.autoAdjustHorizontal || this.options.autoAdjustVertical) {
      this.adjustPosition();
    }
  }

  /**
   * Returns an array of all of the target's "--pos" position classes (e.g. "top", "left", "middle", etc.)
   * @private
   * @returns {Array}
   */
  getPosition() {
    const classes = toArray(this.target.classList);
    return classes
      .filter(c => c.startsWith(`${this.targetBaseClass}--pos-`))
      .map(c => c.replace(`${this.targetBaseClass}--pos-`, ""));
  }

  /**
   * Returns the target's vertical position
   * @private
   * @returns {String|undefined}
   */
  getVerticalPosition() {
    return this.getPosition().find(pos => ["top", "middle", "bottom"].includes(pos));
  }

  /**
   * Returns the target's horizontal position
   * @private
   * @returns {String|undefined}
   */
  getHorizontalPosition() {
    return this.getPosition().find(pos => ["left", "center", "right"].includes(pos));
  }

  /**
   * Replace all existing "--pos" classes with ones derived from the supplied positions
   * @private
   * @param {...String} positions - One or more position strings (e.g. "top", "left", "middle", etc.)
   */
  applyPositionClasses(...positions) {
    const classes = toArray(this.target.classList);
    const classesToRemove = classes.filter(className => className.startsWith(`${this.targetBaseClass}--pos-`));

    classesToRemove.forEach(className => {
      this.target.classList.remove(className);
    });
    positions.forEach(pos => {
      this.target.classList.add(`${this.targetBaseClass}--pos-${pos}`);
    });
  }

  /**
   * Parse the target element's "--pos" classes and position it relative to its trigger element
   * @private
   */
  applyPositionStyles() {
    const vPos = this.getVerticalPosition();
    const hPos = this.getHorizontalPosition();

    // Set vertical position
    if (vPos === "top") {
      this.target.style.top = Math.round(this.trigger.offsetTop - this.target.offsetHeight) + "px";
    }
    if (vPos === "bottom") {
      this.target.style.top = Math.round(this.trigger.offsetTop + this.trigger.offsetHeight) + "px";
    }
    if (vPos === "middle") {
      this.target.style.top = Math.round(this.trigger.offsetTop + (this.trigger.offsetHeight / 2) - (this.target.offsetHeight / 2)) + "px";
    }

    // Set horizontal position
    if ((vPos === "top" || vPos === "bottom") && hPos === "left") {
      this.target.style.left = Math.round(this.trigger.offsetLeft) + "px";
    }
    if ((vPos === "top" || vPos === "bottom") && hPos === "center") {
      this.target.style.left = Math.round(this.trigger.offsetLeft + (this.trigger.offsetWidth / 2) - (this.target.offsetWidth / 2)) + "px";
    }
    if ((vPos === "top" || vPos === "bottom") && hPos === "right") {
      this.target.style.left = Math.round(this.trigger.offsetLeft + this.trigger.offsetWidth - this.target.offsetWidth) + "px";
    }
    if (vPos === "middle" && hPos === "left") {
      this.target.style.left = Math.round(this.trigger.offsetLeft - this.target.offsetWidth) + "px";
    }
    if (vPos === "middle" && hPos === "right") {
      this.target.style.left = Math.round(this.trigger.offsetLeft + this.trigger.offsetWidth) + "px";
    }
  }

  /**
   * Update both the "--pos" classes as well as the inline position styles
   * @param {Array} positions
   */
  applyPosition(...positions) {
    this.applyPositionClasses(...positions);
    this.applyPositionStyles();
  }

  /**
   * Check whether the target element extends beyond the boundaries of the
   * viewport and adjust its position accordingly. The "pos" data attribute
   * will not be modified here, so we'll be able to use it to revert back to
   * the preferred position later.
   * @private
   */
  adjustPosition() {
    // Adjust position if target element extends beyound the bounds of the viewport
    const viewportWidth = document.documentElement.clientWidth;
    let targetRect = this.target.getBoundingClientRect();
    let vPos = this.getVerticalPosition();
    let hPos = this.getHorizontalPosition();

    // Check whether target element is being cut off by the left or right edge of the viewport
    if (this.options.autoAdjustHorizontal) {
      if (targetRect.left < 0) {
        // Target element is being cut off by the left edge of the viewport
        if (vPos === "top") {
          this.applyPosition("top", "center");
        } else if (vPos === "middle") {
          this.applyPosition("middle", "right");
        } else if (vPos === "bottom") {
          this.applyPosition("bottom", "center");
        }
        // Adjust it again if it's still too far left
        targetRect = this.target.getBoundingClientRect();
        vPos = this.getVerticalPosition();
        hPos = this.getHorizontalPosition();

        if (targetRect.left < 0) {
          if (vPos === "top") {
            this.applyPosition("top", "left");
          } else if (vPos === "bottom") {
            this.applyPosition("bottom", "left");
          }
        }
      } else if (targetRect.right > viewportWidth) {
        // Target element is being cut off by the right edge of the viewport
        if (vPos === "top") {
          this.applyPosition("top", "center");
        } else if (vPos === "middle") {
          this.applyPosition("middle", "left");
        } else if (vPos === "bottom") {
          this.applyPosition("bottom", "center");
        }
        // Adjust it again if it's still too far right
        targetRect = this.target.getBoundingClientRect();
        vPos = this.getVerticalPosition();
        hPos = this.getHorizontalPosition();

        if (targetRect.right > viewportWidth) {
          if (vPos === "top") {
            this.applyPosition("top", "right");
          } else if (vPos === "bottom") {
            this.applyPosition("bottom", "right");
          }
        }
      }
    }

    // Check whether target element is being cut off by the top or bottom edge of the viewport
    if (this.options.autoAdjustVertical) {
      vPos = this.getVerticalPosition();
      hPos = this.getHorizontalPosition();

      let observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.intersectionRatio < 1) {
              // Target element is still being cut off (it's less than 100% visible)
              if (vPos === "top") {
                // Flip the target element from the top to the bottom
                if (hPos === "left") {
                  this.applyPosition("bottom", "left");
                } else if (hPos === "center") {
                  this.applyPosition("bottom", "center");
                } else if (hPos === "right") {
                  this.applyPosition("bottom", "right");
                }
              } else if (vPos === "middle") {
                // TODO: in this scenario, we can't know whether the target
                // element is being cut off by the top or bottom edge of the
                // viewport, so how can we decide whether to reposition it
                // above or below the trigger element?
              } else if (vPos === "bottom") {
                // Flip the target element from the bottom to the top
                if (hPos === "left") {
                  this.applyPosition("top", "left");
                } else if (hPos === "center") {
                  this.applyPosition("top", "center");
                } else if (hPos === "right") {
                  this.applyPosition("top", "right");
                }
              }
            }
          }
        });
        observer.disconnect();
        observer = null;
      });
      observer.observe(this.target);
    }
  }

}
